import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  title: string;
  body?: string;
  /** The next thing to do, e.g. "Add your first card" (a Button or Link). */
  action?: ReactNode;
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  className?: string;
}

/** "Nothing here yet → do this next" (T11-01): never a dead end. */
export function EmptyState({ title, body, action, icon: Icon, className }: EmptyStateProps) {
  return (
    <section
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-10 text-center",
        className,
      )}
    >
      {Icon ? <Icon className="size-touch text-muted-foreground" aria-hidden /> : null}
      <h2 className="text-lg font-semibold">{title}</h2>
      {body ? <p className="max-w-prose text-sm text-muted-foreground">{body}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </section>
  );
}

export function LoadingState({ label, className }: { label?: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      data-slot="loading-state"
      className={cn("flex flex-col gap-2 py-6", className)}
    >
      <span className="sr-only">{label ?? t("states.loading")}</span>
      <span aria-hidden className="h-4 w-2/3 animate-pulse rounded bg-muted" />
      <span aria-hidden className="h-4 w-1/2 animate-pulse rounded bg-muted" />
      <span aria-hidden className="h-4 w-3/5 animate-pulse rounded bg-muted" />
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  body?: string;
  onRetry?: () => void;
  className?: string;
  children?: ReactNode;
}

export function ErrorState({ title, body, onRetry, className, children }: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <section
      role="alert"
      data-slot="error-state"
      className={cn(
        "flex flex-col items-start gap-2 rounded-lg border border-status-problem p-4",
        className,
      )}
    >
      <h2 className="font-semibold text-status-problem">{title ?? t("states.error_title")}</h2>
      <p className="text-sm">{body ?? t("states.error_body")}</p>
      {children}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-touch rounded-md border px-4 text-sm font-medium hover:bg-accent"
        >
          {t("states.retry")}
        </button>
      ) : null}
    </section>
  );
}
